'use client'
import { useId } from 'react'
import { PrismicNextImage } from '@prismicio/next'
import { PrismicRichText } from '@prismicio/react'
import * as Accordion from '@radix-ui/react-accordion'

import { cn } from '@/utils/twMerge'
import type { PricingFaqSlice } from '@/types/prismic'

type PricingFaqProps = {
  data: PricingFaqSlice
}

const PricingFaqComponent = ({ data }: PricingFaqProps) => {
  const sectionTitleId = useId()
  const items = data.items ?? []

  return (
    <section className="pricing-faq" aria-labelledby={sectionTitleId}>
      <div className="container">
        <div className="pricing-faq-wrapper grid gap-8 lg:grid-cols-7">
          <div className="pricing-faq-wrapper-left lg:col-span-2" id={sectionTitleId}>
            <PrismicRichText field={data.primary.title} />
            <PrismicNextImage field={data.primary.icon} fallbackAlt="" />
          </div>
          {items.length > 0 && (
            <div className="pricing-faq-wrapper-right lg:col-span-5">
              <Accordion.Root type="single" collapsible defaultValue="0" className="flex flex-col gap-3">
                {items.map((item, index) => (
                  <Accordion.Item
                    key={`pricing-faq-${index}`}
                    value={String(index)}
                    className="group/item rounded-md border border-primary-ink-6 transition-colors has-[:focus-visible]:border-primary-green-150">
                    <Accordion.Header>
                      <Accordion.Trigger className="group flex w-full items-center justify-between px-5 py-4 text-left outline-none focus-visible:outline-none">
                        <span className="pr-4 text-base font-medium text-primary-ink-100 transition-colors group-has-[:focus-visible]/item:text-primary-green-150">
                          {item.title}
                        </span>
                        <svg
                          aria-hidden="true"
                          className={cn(
                            'h-5 w-5 shrink-0 text-primary-ink-30 transition-transform duration-300 ease-in-out group-data-[state=open]:rotate-180',
                          )}
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                        </svg>
                      </Accordion.Trigger>
                    </Accordion.Header>
                    <Accordion.Content className="overflow-hidden transition-all duration-300 ease-in-out data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                      <div className="border-t border-primary-ink-6 px-5 pb-4 pt-3">
                        <PrismicRichText field={item.content} />
                      </div>
                    </Accordion.Content>
                  </Accordion.Item>
                ))}
              </Accordion.Root>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default PricingFaqComponent
